import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, Navigate, Outlet } from "react-router-dom";
import { LayoutDashboard, Calendar, Inbox, Package, FileText, LogOut, ExternalLink, Users, CalendarDays, Settings, Mail } from "lucide-react";
import Logo from "./Logo";
import { useAuth } from "../context/AuthContext";
import NotificationBell from "./NotificationBell";
import { api } from "../lib/api";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/reservations", label: "Réservations", icon: Calendar, badgeKey: "reservations_unseen" },
  { to: "/admin/planning", label: "Planning", icon: CalendarDays },
  { to: "/admin/clients", label: "Clients", icon: Users },
  { to: "/admin/contacts", label: "Demandes", icon: Inbox, badgeKey: "contacts_new" },
  { to: "/admin/emails", label: "Emails", icon: Mail },
  { to: "/admin/blog", label: "Blog", icon: FileText },
  { to: "/admin/content", label: "Contenu", icon: Package },
  { to: "/admin/parametres", label: "Paramètres", icon: Settings },
];

export default function AdminLayout() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState({});

  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/admin/kpis").then((r) => setKpis(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [user]);

  if (!ready) {
    return (
      <div className="min-h-screen bg-fegray-light flex items-center justify-center">
        <div className="text-fegray text-sm uppercase tracking-wider">Chargement…</div>
      </div>
    );
  }
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-fegray-light flex">
      <aside className="w-64 bg-white border-r border-gray-100 flex flex-col" data-testid="admin-sidebar">
        <Link to="/admin" className="px-6 py-5 border-b border-gray-100 flex items-center">
          <Logo size="xs" />
        </Link>

        <nav className="p-4 flex-1 space-y-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            const badge = n.badgeKey ? kpis[n.badgeKey] : 0;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`admin-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? "bg-fegreen-light text-fegreen"
                      : "text-fegray hover:bg-gray-50 hover:text-fegray-dark"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={1.8} />
                <span className="flex-1">{n.label}</span>
                {badge > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-fegreen text-white text-[10px] font-extrabold rounded-full">
                    {badge > 9 ? "9+" : badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-gray-100 space-y-2">
          <Link to="/" target="_blank" className="flex items-center gap-2 text-xs text-fegray hover:text-fegreen uppercase tracking-wider px-4 py-2">
            <ExternalLink className="w-3 h-3" /> Voir le site
          </Link>
          <button
            onClick={handleLogout}
            data-testid="admin-logout"
            className="flex items-center gap-2 text-xs text-fegray hover:text-red-500 uppercase tracking-wider px-4 py-2 w-full"
          >
            <LogOut className="w-3 h-3" /> Déconnexion
          </button>
          <div className="px-4 pt-2 text-[10px] text-fegray/60 truncate">{user.email}</div>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        <div className="sticky top-0 z-40 bg-fegray-light/90 backdrop-blur-md border-b border-gray-100 px-8 lg:px-10 py-3 flex items-center justify-end gap-3">
          <NotificationBell />
        </div>
        <Outlet />
      </main>
    </div>
  );
}
